import { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(coverUrl) {
  if (!coverUrl) return null;
  if (coverUrl.startsWith("http")) return coverUrl;
  return `${BACKEND_URL}${coverUrl}`;
}

export default function CoverUpload({ bookId, coverUrl, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const displayUrl = resolveUrl(coverUrl);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Max 6MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/books/${bookId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Cover uploaded");
      onUploaded && onUploaded(data.cover_image_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex gap-4 items-start" data-testid="cover-upload">
      <div
        className="w-32 h-44 rounded overflow-hidden flex items-center justify-center"
        style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}
      >
        {displayUrl ? (
          <img src={displayUrl} alt="Book cover" className="w-full h-full object-cover" data-testid="cover-preview" />
        ) : (
          <ImageIcon className="w-8 h-8" style={{ color: "var(--bf-text-muted)" }} />
        )}
      </div>
      <div>
        <p className="text-sm font-medium mb-1">Book cover</p>
        <p className="text-xs mb-3" style={{ color: "var(--bf-text-muted)" }}>
          JPG, PNG, or WebP. Up to 6MB. Recommended 800×1200.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
          data-testid="cover-file-input"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="cover-upload-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          {uploading ? "Uploading…" : displayUrl ? "Replace cover" : "Upload cover"}
        </Button>
      </div>
    </div>
  );
}

export { resolveUrl };
